import React, { useState, createElement, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Stethoscope, Droplets, GraduationCap, ArrowRight, QrCode, Download } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { BillvastLogo } from '../ui/BillvastLogo';
const causes = [{
  title: 'Medical Fund for Amara',
  category: 'Medical',
  icon: Stethoscope,
  iconColor: 'text-red-500 bg-red-50',
  raised: 450000,
  target: 750000,
  donors: 87,
  image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=400&q=80'
}, {
  title: 'Feed 1,000 Displaced Families',
  category: 'Humanitarian',
  icon: Droplets,
  iconColor: 'text-blue-500 bg-blue-50',
  raised: 1800000,
  target: 3000000,
  donors: 312,
  image: 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&w=400&q=80'
}, {
  title: 'School Supplies for Kano Kids',
  category: 'Education',
  icon: GraduationCap,
  iconColor: 'text-purple-500 bg-purple-50',
  raised: 180000,
  target: 500000,
  donors: 45,
  image: 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=400&q=80'
}];
export function CausesSection() {
  const [qrCause, setQrCause] = useState<(typeof causes)[0] | null>(null);
  return <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-id="element-30">
      <div className="max-w-7xl mx-auto" data-id="element-31">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-32">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-bold text-brand-text-dark mb-3" data-id="element-33">
            Causes That Need{' '}
            <span className="text-brand-primary" data-id="element-34">Your Support</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-brand-text-light text-lg" data-id="element-35">
            Join thousands of Nigerians making a difference — every naira goes
            to the cause.
          </motion.p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-36">
          {causes.map((cause, index) => {
          const progress = Math.min(Math.round(cause.raised / cause.target * 100), 100);
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.08
          }} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group" data-id="element-37">
                <div className="relative h-40 overflow-hidden" data-id="element-38">
                  <img src={cause.image} alt={cause.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-39" />
                  <div className="absolute inset-0 bg-black/10" data-id="element-40" />
                  <span className={`absolute top-3 left-3 text-xs font-semibold px-2.5 py-1 rounded-full ${cause.iconColor}`} data-id="element-41">
                    {cause.category}
                  </span>
                </div>
                <div className="p-5 space-y-3" data-id="element-42">
                  <h4 className="font-bold text-brand-text-dark leading-tight" data-id="element-43">
                    {cause.title}
                  </h4>
                  <div data-id="element-44">
                    <div className="flex justify-between text-sm mb-1.5" data-id="element-45">
                      <span className="font-semibold text-brand-primary" data-id="element-46">
                        ₦{cause.raised.toLocaleString()}
                      </span>
                      <span className="text-gray-400 text-xs" data-id="element-47">
                        of ₦{cause.target.toLocaleString()}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5" data-id="element-48">
                      <div className="bg-brand-primary h-1.5 rounded-full transition-all duration-700" style={{
                    width: `${progress}%`
                  }} data-id="element-49" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-brand-text-light" data-id="element-50">
                    <span className="flex items-center gap-1" data-id="element-51">
                      <Heart size={12} data-id="element-52" /> {cause.donors} donors
                    </span>
                    <span className="font-medium text-brand-primary" data-id="element-53">
                      {progress}% funded
                    </span>
                  </div>
                  <div className="flex gap-2 pt-1" data-id="element-54">
                    <Link to="/donate" className="flex-1 py-2.5 bg-brand-primary hover:bg-brand-primary-medium text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-55">
                      <Heart size={16} data-id="element-56" /> Donate
                    </Link>
                    <button onClick={() => setQrCause(cause)} className="px-3 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-xl transition-colors flex items-center justify-center" title="Generate QR Code" data-id="element-57">
                      <QrCode size={18} data-id="element-58" />
                    </button>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>

        <div className="mt-10 text-center" data-id="element-59">
          <Link to="/donate" className="inline-flex items-center gap-2 px-6 py-3 bg-brand-primary text-white font-semibold rounded-xl hover:bg-brand-primary-medium transition-colors" data-id="element-60">
            View All Verified Causes <ArrowRight size={18} data-id="element-61" />
          </Link>
        </div>
      </div>

      {/* QR Code Modal */}
      <Modal isOpen={!!qrCause} onClose={() => setQrCause(null)} title="Scan to Donate" maxWidth="max-w-sm" data-id="element-62">
        {qrCause && <div className="flex flex-col items-center text-center space-y-6 py-4" data-id="element-63">
            <div data-id="element-64">
              <h3 className="font-bold text-brand-text-dark text-lg leading-tight mb-1" data-id="element-65">
                {qrCause.title}
              </h3>
              <p className="text-sm text-brand-text-light" data-id="element-66">
                Scan with any camera app to donate directly to this cause.
              </p>
            </div>

            <div className="relative p-4 bg-white border-2 border-gray-100 rounded-2xl shadow-sm inline-block" data-id="element-67">
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(`https://billvast.com/donate?cause=${qrCause.title}`)}`} alt="QR Code" className="w-48 h-48" data-id="element-68" />
              {/* Center Logo Overlay */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-69">
                <div className="bg-white p-1.5 rounded-lg shadow-sm border border-gray-100" data-id="element-70">
                  <img src="https://cdn.magicpatterns.com/uploads/4AEfCpc54BiEMtMYwDkd9o/Billvast_logo.png" alt="Billvast" className="w-8 h-8 object-contain" data-id="element-71" />
                </div>
              </div>
            </div>

            <button onClick={() => {
          const link = document.createElement('a');
          link.href = `https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${encodeURIComponent(`https://billvast.com/donate?cause=${qrCause.title}`)}`;
          link.download = `QR-${qrCause.title.replace(/\s+/g, '-')}.png`;
          link.target = '_blank';
          link.click();
        }} className="w-full py-3 bg-gray-900 hover:bg-black text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-72">
              <Download size={18} data-id="element-73" /> Download QR Code
            </button>
          </div>}
      </Modal>
    </section>;
}